/** One chat under a dispatcher. `mine` means the logged-in user already has it. */
export type DispatcherSectionChat = {
  jid: string
  name: string
  mine: boolean
}

export type DispatcherSection = {
  dispatcherJid: string
  label: string
  chats: DispatcherSectionChat[]
}

/** Fleet overview's name for the vehicles no dispatcher keeps. A manager's
 * own bookmarks do not count: managers keep every vehicle, so only the
 * dispatchers' bookmarks say whether a vehicle is assigned. */
export const UNASSIGNED_SECTION_JID = "unassigned"
export const UNASSIGNED_SECTION_LABEL = "Nieprzypisane"

export type DispatcherSectionsInput = {
  byDispatcher: Record<string, string[]>
  /** Chat JID -> {name, groupName}, from the account's chat list. */
  accountChatsByJid: Record<
    string,
    { name: string; groupName: string; hidden?: boolean }
  >
  myChatJids: string[]
  /** Chats the logged-in user archived. They are gone from the plain list, so
   * this view — a second face on the same list — must not resurface them. */
  archivedChatJids: string[]
  /** Dispatcher JID -> display name, from the office profiles. */
  dispatcherNames: Record<string, string>
  myJid: string
  /** Close the list with the vehicles no dispatcher keeps. Only a fleet
   * manager asks for it: `byDispatcher` covers every dispatcher, so for them
   * "nobody's" really means unassigned, while a dispatcher peeking at the
   * others would just see their own vehicles listed twice. */
  includeUnassigned?: boolean
}

const localPart = (jid: string) => jid.split("@")[0]

/** Only vehicle chats belong in dispatcher sections; "Groups" contains
 * office, support, and automation rooms. */
const isVehicleChat = (chat: { groupName: string; hidden?: boolean }) =>
  chat.groupName !== "Groups" && !chat.hidden

export const buildDispatcherSections = ({
  byDispatcher,
  accountChatsByJid,
  myChatJids,
  archivedChatJids,
  dispatcherNames,
  myJid,
  includeUnassigned = false,
}: DispatcherSectionsInput): DispatcherSection[] => {
  const mine = new Set(myChatJids)
  const archived = new Set(archivedChatJids)

  const toSectionChats = (chatJids: string[]): DispatcherSectionChat[] =>
    chatJids
      // A bookmark can outlive its room; without a name there is nothing to
      // show and nothing to open, so it is left out.
      .filter((chatJid) => accountChatsByJid[chatJid] !== undefined)
      .filter((chatJid) => isVehicleChat(accountChatsByJid[chatJid]))
      .filter((chatJid) => !archived.has(chatJid))
      .map((chatJid) => ({
        jid: chatJid,
        name: accountChatsByJid[chatJid].name,
        mine: mine.has(chatJid),
      }))
      .sort((left, right) => left.name.localeCompare(right.name))

  const sections = Object.entries(byDispatcher)
    // Drop the logged-in user's own section entirely.
    .filter(([dispatcherJid]) => dispatcherJid !== myJid)
    .map(([dispatcherJid, chatJids]) => ({
      dispatcherJid,
      label: dispatcherNames[dispatcherJid] || localPart(dispatcherJid),
      chats: toSectionChats(chatJids),
    }))
    .filter((section) => section.chats.length > 0)

  // Sort sections alphabetically by label.
  sections.sort((left, right) => left.label.localeCompare(right.label))

  if (!includeUnassigned) return sections

  const assigned = new Set(Object.values(byDispatcher).flat())
  const unassigned = toSectionChats(
    Object.keys(accountChatsByJid).filter((chatJid) => !assigned.has(chatJid)),
  )
  // Kept last, after the alphabetical dispatchers, like fleet overview does.
  if (unassigned.length > 0)
    sections.push({
      dispatcherJid: UNASSIGNED_SECTION_JID,
      label: UNASSIGNED_SECTION_LABEL,
      chats: unassigned,
    })

  return sections
}

export type DispatcherOption = { jid: string; label: string }

/** Everyone a chat can be handed to, in the order the sections use. The
 * logged-in user is left out: a manager keeps no vehicles. */
export const buildDispatcherOptions = ({
  byDispatcher,
  dispatcherNames,
  myJid,
}: Pick<
  DispatcherSectionsInput,
  "byDispatcher" | "dispatcherNames" | "myJid"
>): DispatcherOption[] =>
  Object.keys(byDispatcher)
    .filter((dispatcherJid) => dispatcherJid !== myJid)
    .map((jid) => ({ jid, label: dispatcherNames[jid] || localPart(jid) }))
    .sort((left, right) => left.label.localeCompare(right.label))
